import React, { useState } from 'react';
import { 
  Briefcase, Users, Share2, Code, Send, ChevronDown, 
  CheckCircle2, ArrowRight, MapPin, Building, Loader2 
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

// --- CAREERS DATA ---
const jobOpenings = [
  {
    id: 1,
    category: "Vendor Management",
    role: "Vendor Relations Manager",
    icon: Building,
    color: "#0284c7",
    bg: "#e0f2fe",
    darkBg: "#082f49",
    type: "Full-Time",
    location: "Remote / Hybrid",
    jd: "We are looking for a dynamic individual to build and maintain relationships with our network of hotels, stays, transport providers, and local guides. You will be responsible for negotiating contracts, onboarding new partners, ensuring quality compliance, and expanding the Odessey partner ecosystem across India."
  },
  {
    id: 2,
    category: "Customer Management",
    role: "Travel Experience Specialist",
    icon: Users,
    color: "#15803d",
    bg: "#dcfce7",
    darkBg: "#064e3b",
    type: "Full-Time",
    location: "Remote",
    jd: "Be the voice of Odessey! You will assist travellers in planning their custom itineraries, resolving queries, and providing real-time support during their trips. We need someone with deep empathy, excellent communication skills, and a passion for ensuring a 5-star travel experience."
  },
  {
    id: 3,
    category: "Social Media Management",
    role: "Social Media Strategist",
    icon: Share2,
    color: "#be185d",
    bg: "#fce7f3",
    darkBg: "#500724",
    type: "Full-Time",
    location: "Remote / Hybrid",
    jd: "Drive Odessey's digital presence. You will create engaging, wanderlust-inducing travel content, manage our Instagram and YouTube channels, run ad campaigns, and build a vibrant community of explorers. Must have an eye for design and a pulse on current social media trends."
  },
  {
    id: 4,
    category: "Website and App Backend Support",
    role: "Backend Developer (Node.js/Python)",
    icon: Code,
    color: "#6d28d9",
    bg: "#f3e8ff",
    darkBg: "#3b0764",
    type: "Full-Time",
    location: "Remote",
    jd: "Maintain and optimize the core infrastructure of the Odessey platform. You will manage map APIs (Ola Maps, OSM), handle database operations, optimize route-planning algorithms, and ensure high availability for our travel engine. Experience with RESTful APIs and cloud deployment is required."
  }
];

const CareersPage = () => {
  const { isDark } = useTheme(); // 👈 Access Theme State
  const [activeTab, setActiveTab] = useState('careers'); // 'careers' or 'partner'
  const [expandedJob, setExpandedJob] = useState(null);
  
  // Form State for Partners
  const [formData, setFormData] = useState({
    businessName: '', contactPerson: '', email: '', phone: '',
    category: 'Hotel / Lodge / Resort', location: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const toggleJob = (id) => {
    setExpandedJob(expandedJob === id ? null : id);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({ businessName: '', contactPerson: '', email: '', phone: '', category: 'Hotel / Lodge / Resort', location: '', message: '' });
      setTimeout(() => setSubmitSuccess(false), 5000);
    }, 1500);
  };

  const inputStyle = {
    width: '100%', 
    height: '50px',
    padding: '0 15px', 
    borderRadius: '10px', 
    border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', 
    outline: 'none', 
    fontSize: '15px',
    boxSizing: 'border-box',
    backgroundColor: isDark ? '#111827' : 'white',
    color: isDark ? 'white' : '#111827'
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: '80px', transition: 'background-color 0.3s' }}>
      
      {/* HERO SECTION */}
      <div style={{ backgroundColor: isDark ? '#030712' : '#111827', padding: '100px 16px 50px 16px', textAlign: 'center', color: 'white', transition: 'background-color 0.3s' }}>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 42px)', fontWeight: '900', marginBottom: '15px' }}>Grow With Odessey</h1>
        <p style={{ fontSize: '15px', color: '#9ca3af', maxWidth: '600px', margin: '0 auto', lineHeight: '1.5' }}>
          Whether you want to build the future of travel with our internal team, or showcase your property to our travellers, you belong here.
        </p>
      </div>

      <div style={{ maxWidth: '900px', margin: '-25px auto 0 auto', padding: '0 16px', position: 'relative', zIndex: 10, boxSizing: 'border-box' }}>
        
        {/* TAB TOGGLE */}
        <div style={{ display: 'flex', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '50px', padding: '6px', boxShadow: isDark ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.05)', marginBottom: '35px', border: isDark ? '1px solid #374151' : 'none', transition: 'background-color 0.3s' }}>
          <button 
            onClick={() => setActiveTab('careers')}
            style={{ flex: 1, padding: '12px 16px', borderRadius: '40px', fontSize: '14px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            backgroundColor: activeTab === 'careers' ? '#0284c7' : 'transparent', color: activeTab === 'careers' ? 'white' : (isDark ? '#9ca3af' : '#6b7280') }}
          >
            <Briefcase size={18} /> Join Our Team
          </button>
          <button 
            onClick={() => setActiveTab('partner')}
            style={{ flex: 1, padding: '12px 16px', borderRadius: '40px', fontSize: '14px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            backgroundColor: activeTab === 'partner' ? '#16a34a' : 'transparent', color: activeTab === 'partner' ? 'white' : (isDark ? '#9ca3af' : '#6b7280') }}
          >
            <Building size={18} /> Partner With Us
          </button>
        </div>

        {/* CONTENT AREA */}
        <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
          
          {/* --- TAB 1: CAREERS --- */}
          {activeTab === 'careers' && (
            <div>
              <div style={{ marginBottom: '25px', textAlign: 'center' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827' }}>Open Positions</h2>
                <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280' }}>Help us redefine road trips and travel experiences across India.</p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {jobOpenings.map((job) => (
                  <div key={job.id} style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '16px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', overflow: 'hidden', boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.02)', transition: 'all 0.3s' }}>
                    <div onClick={() => toggleJob(job.id)} style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ width: '45px', height: '45px', borderRadius: '12px', backgroundColor: isDark ? job.darkBg : job.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <job.icon size={20} color={job.color} />
                        </div>
                        <div>
                          <p style={{ fontSize: '11px', fontWeight: 'bold', color: job.color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{job.category}</p>
                          <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>{job.role}</h3>
                          <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: '500', flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><Briefcase size={12} /> {job.type}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><MapPin size={12} /> {job.location}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronDown size={20} color="#9ca3af" style={{ transform: expandedJob === job.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                    </div>

                    <div style={{ maxHeight: expandedJob === job.id ? '500px' : '0', overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
                      <div style={{ padding: '0 20px 20px 20px', color: isDark ? '#d1d5db' : '#4b5563', fontSize: '14px', lineHeight: '1.6', boxSizing: 'border-box' }}>
                        <div style={{ width: '100%', height: '1px', backgroundColor: isDark ? '#374151' : '#f3f4f6', marginBottom: '15px' }} />
                        <h4 style={{ fontSize: '13px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '6px' }}>Role Overview</h4>
                        <p style={{ marginBottom: '15px' }}>{job.jd}</p>
                        <a href={`mailto:careers@odessey.in?subject=Application for ${job.role}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#111827', color: 'white', textDecoration: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '13px', transition: 'background 0.2s', border: isDark ? '1px solid #4b5563' : 'none' }}>
                          Apply via Email <ArrowRight size={14} />
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* --- TAB 2: PARTNER ONBOARDING --- */}
          {activeTab === 'partner' && (
            <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', padding: '25px 20px', boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.5)' : '0 10px 40px rgba(0,0,0,0.05)', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
              <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>Become an Odessey Partner</h2>
                <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280' }}>Are you a hotel, transport agency, or local guide? Register your services with us to reach thousands of travellers.</p>
              </div>

              {submitSuccess ? (
                <div style={{ textAlign: 'center', padding: '30px 15px', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', borderRadius: '14px', border: isDark ? '1px solid #065f46' : '1px solid #bbf7d0' }}>
                  <CheckCircle2 size={50} color="#16a34a" style={{ margin: '0 auto 12px auto' }} />
                  <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: isDark ? '#a7f3d0' : '#15803d', marginBottom: '8px' }}>Enquiry Sent Successfully!</h3>
                  <p style={{ fontSize: '14px', color: isDark ? '#d1fae5' : '#166534', margin: 0 }}>Our Vendor Management team will contact you within 24-48 hours to discuss partnership opportunities.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Business Name *</label>
                      <input required type="text" placeholder="e.g. Royal Palace Hotel" value={formData.businessName} onChange={e=>setFormData({...formData, businessName: e.target.value})} style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Contact Person *</label>
                      <input required type="text" placeholder="Your Name" value={formData.contactPerson} onChange={e=>setFormData({...formData, contactPerson: e.target.value})} style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Email Address *</label>
                      <input required type="email" placeholder="contact@business.com" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Phone Number *</label>
                      <input required type="tel" placeholder="+91 XXXXX XXXXX" value={formData.phone} onChange={e=>setFormData({...formData, phone: e.target.value})} style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Service Category *</label>
                      <select value={formData.category} onChange={e=>setFormData({...formData, category: e.target.value})} style={{ ...inputStyle, cursor: 'pointer' }}>
                        <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }}>Hotel / Lodge / Resort</option>
                        <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }}>Transport / Cab Agency</option>
                        <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }}>Local Tour Guide</option>
                        <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }}>Adventure / Activity Organizer</option>
                        <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }}>Restaurant / Cafe</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Primary Location *</label>
                      <div style={{ position: 'relative' }}>
                        <MapPin size={16} color="#9ca3af" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input required type="text" placeholder="e.g. Udaipur, Rajasthan" value={formData.location} onChange={e=>setFormData({...formData, location: e.target.value})} style={{ ...inputStyle, paddingLeft: '38px' }} />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '6px' }}>Briefly describe your services</label>
                    <textarea rows="4" placeholder="Tell us about what you offer..." value={formData.message} onChange={e=>setFormData({...formData, message: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }}></textarea>
                  </div>

                  <button type="submit" disabled={isSubmitting} style={{ padding: '14px', borderRadius: '12px', backgroundColor: '#16a34a', color: 'white', fontWeight: 'bold', fontSize: '15px', border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.2s', marginTop: '10px' }}>
                    {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <><Send size={18} /> Submit Partnership Enquiry</>}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default CareersPage;